/**
 * The contents of this file are subject to the license and copyright
 * detailed in the LICENSE and NOTICE files at the root of the source
 * tree and available online at
 *
 * http://www.dspace.org/license/
 */

import { Injectable } from '@angular/core';
import {
  Observable,
  of,
} from 'rxjs';
import {
  catchError,
  map,
  tap,
  timeout,
} from 'rxjs/operators';

import { PaginationComponentOptions } from '../../pagination/pagination-component-options.model';
import { PaginatedSearchOptions } from '../../search/models/paginated-search-options.model';
import { SearchService } from '../../../core/shared/search/search.service';
import { DSpaceObjectType } from '../../../core/shared/dspace-object-type.model';
import { Community } from '../../../core/shared/community.model';
import { getFirstCompletedRemoteData } from '../../../core/shared/operators';
import { MenuItemType } from '../menu-item-type.model';
import {
  AbstractMenuProvider,
  PartialMenuSection,
} from '../menu-provider.model';

/**
 * Configuration for the Researcher menu
 * Change COMMUNITY_NAME to match your community's title
 */
const RESEARCHER_COMMUNITY_NAME = 'Researcher';

/**
 * Menu provider to create the "Researcher" menu section in the public navbar
 * Dynamically finds the community by name and links to its search page
 */
@Injectable()
export class ResearcherMenuProvider extends AbstractMenuProvider {

  constructor(
    private searchService: SearchService,
  ) {
    super();
  }

  public getSections(): Observable<PartialMenuSection[]> {
    // Search for all communities (empty query)
    const pagination = Object.assign(new PaginationComponentOptions(), {
      pageSize: 50, // Get more communities to inspect
      currentPage: 1,
    });

    const searchOptions = new PaginatedSearchOptions({
      query: '', // Empty query to get all communities
      dsoTypes: [DSpaceObjectType.COMMUNITY],
      pagination,
    });
    
    return this.searchService.search(searchOptions).pipe(
 
      getFirstCompletedRemoteData(),
      map((rd) => {
        if (rd.hasSucceeded && rd.payload?.page?.length > 0) {
 
          
          // Try to find the community by name
          const community = rd.payload.page.find((result) => {
            const dso = result.indexableObject as Community;
            return dso?.name?.toLowerCase().includes(RESEARCHER_COMMUNITY_NAME.toLowerCase() );
          });

          if (community) {
            const communityId = (community.indexableObject as Community).id;
            return [
              {
                visible: true,
                model: {
                  type: MenuItemType.LINK,
                  text: 'menu.section.researcher',
                  link: `/communities/${communityId}/search`,
                },
                icon: 'user-graduate',
              },
            ] as PartialMenuSection[];
          }
        }

        // Fallback
        return [
          {
            visible: true,
            model: {
              type: MenuItemType.LINK,
              text: 'menu.section.researcher',
              link: '/search',
            },
            icon: 'user-graduate',
          },
        ] as PartialMenuSection[];
      }),
      catchError(() => {
        return of([
          {
            visible: true,
            model: {
              type: MenuItemType.LINK,
              text: 'menu.section.researcher',
              link: '/search',
            },
            icon: 'user-graduate',
          },
        ] as PartialMenuSection[]);
      }),
    );
  }
}
